import ServiceExec from '@/common/utils/ServiceExec';
import { isPlaceNo } from '@/common/utils/qrPick';

/**
 * 260813 스토어를 거쳐도 QR 코드가 따라오게 하는 이월 — **스토어로 보내는 모든 경로**가 이걸 먼저 부른다.
 *
 * 유저 신고(260813): "간판온 연결하기 눌러도 앱설치화면만 뜨지 안 따라오잖아."
 * 원인은 이월 자체가 아니라 **부르는 데가 한 군데뿐이었던 것** — 랜딩의 스토어 버튼만 코드를 맡기고,
 * 관리 화면('간판ON 연결하기')은 그냥 스토어로 보냈다. 그쪽으로 나간 사람은 설치 후 맹탕 앱을 본다.
 * 그래서 함수로 빼서 스토어 이동 지점이 늘어도 빠질 수 없게 한다.
 *
 * 서버는 IP로 10분만 보관하고 1회 소비하면 지운다(QrController#pending-claim).
 * ⚠️소유 증명이 아니다 — 앱은 이 코드로 정품등록 **화면까지만** 열고 등록은 사장이 누른다.
 * ⚠️실패해도 스토어 이동은 그대로 진행한다. 이월은 편의지 관문이 아니다.
 *
 * @param {string} qrCode   미개통 QR 코드
 * @param {string} [bizName] 고른/적은 상호(120자 컷)
 * @param {string|number} [placeNo] 공공데이터 디렉토리(tblPublicPlace) 식별자 — 숫자 1~18자리일 때만 실린다
 */
export const stashQrCodeForInstall = async (qrCode, bizName, placeNo) => {
  if (!qrCode) return;
  try {
    // 260828 bizName — 검색에 자기 가게(교회)가 없어 직접 적은 상호. 코드와 같은 행에 적히고
    //   설치 후 개통 화면의 이름칸이 이 값으로 채워진다. 없으면 종전대로 코드만 이월된다.
    const name = (bizName || '').trim();
    // 260917 placeNo — 검색에서 고른 가게 식별자(공공데이터 tblPublicPlace). 앱이 이걸로 `/api/qr/place/one` 을 다시 조회해
    //   주소·좌표·전화·업종을 되찾는다. 값을 하나씩 나르지 않는 이유: 그 정보는 이미 우리 DB 에 다 있다.
    //   (260904 카카오 kakaoId 는 계획 2 에서 제거 — BE 도 더는 받지 않는다.) 정수가 아니면 붙이지 않는다(BE parsePlaceNo 와 같은 판정).
    const pno = isPlaceNo(placeNo) ? String(placeNo) : '';
    await ServiceExec.post(`/api/qr/pending-claim?qrCode=${encodeURIComponent(qrCode)}`
      + (name ? `&bizName=${encodeURIComponent(name.slice(0, 120))}` : '')
      + (pno ? `&placeNo=${encodeURIComponent(pno)}` : ''));
  } catch (_) {
    /* 무시 — 사장은 앱에서 QR 뒷자리를 직접 입력해도 된다 */
  }
};

// ⚠️market:// 스킴은 웹뷰에서 ERR_UNKNOWN_URL_SCHEME으로 죽는다(260714) — https 스토어 링크만.
const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.appg.jump';
const APPLE_URL = 'https://apps.apple.com/kr/app/id6670776419';

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

/**
 * 260914 iOS 설치 이월 — App Store 에는 Play 의 referrer 가 없다. 스토어로 보내기 직전 클립보드에 마커 URL 을
 * 써 두면 앱 첫 실행 때 네이티브(iOS BranchHandler.readClipboardReferralMarker → captureQrInstallMarker)가 읽어
 * `/more/qr-genuine?code=` 로 onDynamicLink 를 밀어 준다. 추천 `/r/{code}` 클립보드 핸드오프와 같은 방식이며,
 * iOS 16+ 는 첫 실행에 '붙여넣기 허용' 프롬프트가 1회 뜬다(클립보드에 URL 이 있을 때만 — detectPatterns 선검사).
 * ⚠️클립보드 쓰기는 사용자 제스처 안에서만 허용되므로 pending-claim POST(await)보다 **먼저** 부른다.
 * ⚠️실패해도 스토어 이동은 그대로 — pending-claim(서버 IP 보관)이 남는다.
 */
const iosInstallMarker = qrCode => `https://ganpanon.com/more/qr-genuine?code=${encodeURIComponent(qrCode)}`;
const legacyCopy = text => {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    document.execCommand('copy');
    document.body.removeChild(ta);
  } catch (_) { /* 폴백도 실패 — 이월은 편의지 관문이 아니다 */ }
};
export const copyIosInstallMarker = qrCode => {
  if (!qrCode) return;
  const text = iosInstallMarker(qrCode);
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => legacyCopy(text));
      return;
    }
  } catch (_) { /* 아래 폴백 */ }
  legacyCopy(text);
};

/**
 * 260813 "앱 설치 → 로그인 → 바인딩" 한 길 — 스토어로 보내는 버튼은 전부 이 함수 하나로 나간다.
 *
 * 코드는 이중으로 따라간다: ①Play referrer(신규 설치·안드) ②pending-claim 맡김(이미 깔린 앱·iOS).
 * 이미 설치된 사람도 스토어의 '열기'로 앱을 열면 App.vue#resumePendingQrClaim이 코드를 되찾아
 * 로그인 → 정품등록 화면으로 이어준다.
 *
 * ⚠️카톡 인앱 브라우저에서도 이 경로는 **그대로 동작한다**(260813 유저 실측 — PROMO 랜딩으로 실증).
 *   intent:// 차단은 스토어 링크와 무관하다. 탈출(escapeInAppBrowser)·"앱에서 시작을 눌러주세요" 안내
 *   모달 같은 우회를 여기에 얹지 말 것 — 잘못된 전제였고 유저가 걷어냈다.
 *
 * @param {string} qrCode
 * @param {string} [bizName]
 * @param {string|number} [placeNo] 공공데이터 디렉토리 식별자(stashQrCodeForInstall 로 그대로 넘긴다)
 */
export const goStoreWithQr = async (qrCode, bizName, placeNo) => {
  const ios = isIos();
  if (ios) copyIosInstallMarker(qrCode); // 260914 제스처 안에서 먼저(아래 await 뒤면 Safari 가 거부할 수 있다)
  await stashQrCodeForInstall(qrCode, bizName, placeNo);
  window.location.href = ios
    ? APPLE_URL
    : `${PLAY_URL}&referrer=${encodeURIComponent(`qr_genuine=${qrCode}`)}`;
};

/**
 * 260923 대표 지시 — 소비자 '이 가게 저장하기'는 앱 전용. 모바일 웹에서 누르면 스토어로 보낸다.
 *
 * goStoreWithQr 와 일부러 가른다. 그쪽은 **사장 개통** 경로라 pending-claim(코드 맡김)과
 * iOS 클립보드 마커(`/more/qr-genuine`)를 남기는데, 가게를 저장하려던 소비자에게 그게 붙으면
 * 설치 후 첫 화면이 남의 QR 정품등록이 된다. Play referrer(`qr_genuine=`)도 같은 이유로 안 붙인다.
 * 그래서 여기는 개통 이월 없이 스토어 링크만 — 소비자는 앱을 깔고 가게를 다시 찾으면 된다.
 *
 * 260928 대신 **가입 경로**만 따라간다(markQrSignupSource 주석) — 어느 현판에서 가입이 들어왔는지 집계용 라벨이고
 * 화면 이동은 하나도 없다. ①이 브라우저에 표시 ②서버 IP 맡김(iOS·보조 — 앱이 가입 직전에 되찾는다, takeQrSignupIntent)
 * ③Play referrer `utm_content=qr_<코드>`(안드 신규 설치). ③은 `qr_genuine` 가 아니라서 네이티브 개통 이월
 * (PlayInstallReferrerHandler.handleQrReferrer)을 타지 않고, 이미 배포된 앱이 utm_content 를 추천 컨텍스트로 들고 있으면
 * App.vue#captureQrInstallReferrer 가 가입 경로로만 받아 둔다(추천 추적에는 흘리지 않는다).
 * ⚠️이미 로그인한 손님은 호출부가 코드를 넘기지 않는다 — 가입할 일이 없어 맡긴 행이 남의 가입에 붙는다(260928 교차 검수).
 *
 * @param {string} [qrCode] 손님이 보던 현판 코드(없으면 종전처럼 스토어 링크만)
 */
export const goStoreForConsumer = async qrCode => {
  const source = qrSignupSource(qrCode);
  if (!source) {
    window.location.href = isIos() ? APPLE_URL : PLAY_URL;
    return;
  }
  markQrSignupSource(qrCode);
  await stashQrSignupIntent(qrCode);
  window.location.href = isIos()
    ? APPLE_URL
    : `${PLAY_URL}&referrer=${encodeURIComponent(`utm_source=qr&utm_content=${source}`)}`;
};

/**
 * 260928 손님 가입 경로 — 현판(QR)에서 가입·앱 설치로 넘어간 손님의 가입을 그 현판으로 집계한다.
 *
 * 유저: "아가페 교회에서 회원가입 들어온 건수 있는지 확인해줘" → 기록으로 확정된 건 1명뿐이었다.
 *   예배 시간대 가입 6명이 더 있었지만 가입 경로(tblUser.utmSource)가 비어 있어 확정하지 못했다.
 * 값 = `qr_<코드>`(예: qr_G9J85S6M). 가입 요청(user/actions.js signUp)이 이미 `jump_utm_source` 를 utmSource 로 싣고,
 * 일반·소셜·SSO 가입이 전부 `/api/user/join` 한 곳으로 모여 저장된다 — 여기선 그 키에 적기만 한다.
 * ⚠️가장 최근에 탄 경로로 덮어쓴다(?utm_source 링크와 같은 키·같은 규칙).
 */
const QR_SOURCE_RE = /^qr_[0-9A-Z]{1,12}$/;
export const qrSignupSource = qrCode => {
  const c = String(qrCode || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  return c && c.length <= 12 ? `qr_${c}` : '';
};
export const isQrSignupSource = value => QR_SOURCE_RE.test(String(value || ''));
export const markQrSignupSource = qrCode => {
  const source = qrSignupSource(qrCode);
  if (!source) return;
  try {
    window.localStorage.setItem('jump_utm_source', source);
  } catch (_) {
    /* localStorage 차단 환경 무시 — 집계 한 칸을 놓칠 뿐 */
  }
};

/**
 * 260928 스토어로 가기 직전 서버에 코드 맡기기(같은 IP·60분). 앱이 **가입 직전에** 되찾는다(takeQrSignupIntent).
 * 스토어 이동을 붙잡지 않게 1.5초까지만 기다린다 — 느리면 맡김만 포기하고 그대로 간다.
 */
export const stashQrSignupIntent = async qrCode => {
  const c = qrSignupSource(qrCode).slice(3);
  if (!c) return;
  try {
    await Promise.race([
      ServiceExec.post(`/api/qr/signup-intent?qrCode=${encodeURIComponent(c)}`),
      new Promise(resolve => setTimeout(resolve, 1500))
    ]);
  } catch (_) {
    /* 무시 — 안드로이드는 referrer 로도 따라간다 */
  }
};

/**
 * 260928 가입 직전 되찾기 — 폰 웹에서 현판을 누르고 스토어로 간 손님이면 서버가 같은 IP 로 맡아 둔 현판 코드를 준다.
 * 가입하는 사람만 부르므로(user/actions.js signUp, 가입 경로가 비어 있을 때만) 로그인만 하는 기존 회원·둘러보기 손님이
 * 같은 와이파이의 남의 몫을 가져가지 않는다. 가입을 붙잡지 않게 1.5초까지만 기다린다. 없거나 실패하면 ''.
 * 상태를 바꾸는 호출이라 POST(서버는 같은 출처만 받는다 — QrController#takeSignupIntent).
 */
export const takeQrSignupIntent = async () => {
  try {
    const res = await Promise.race([
      ServiceExec.post('/api/qr/signup-intent/take'),
      new Promise(resolve => setTimeout(() => resolve(null), 1500))
    ]);
    return res && res.status === ServiceExec.status.OK && res.data?.qrCode ? String(res.data.qrCode) : '';
  } catch (_) {
    return '';
  }
};

/**
 * 260928 안드 Play referrer 로 따라온 `qr_<코드>` 를 **코드당 한 번만** 가입 경로로 적는다.
 * Play Install Referrer 는 앱을 지울 때까지 같은 값을 돌려주고 네이티브는 부팅마다 다시 저장하므로,
 * 표시 없이 쓰면 부팅마다 덮어써 그 뒤에 탄 더 최근 경로(?utm_source 링크 등)를 지운다(260928 교차 검수).
 * @returns {boolean} qr_ 값이었으면 true(호출부는 consume 만 하고 끝낸다)
 */
export const markQrInstallReferrerOnce = value => {
  const rc = String(value || '');
  if (!isQrSignupSource(rc)) return false;
  const KEY = 'jump.qr.referrerDone';
  try {
    if (window.localStorage.getItem(KEY) === rc) return true;
    window.localStorage.setItem(KEY, rc);
  } catch (_) {
    /* 저장소 차단 — 아래 표시만 한다 */
  }
  markQrSignupSource(rc.slice(3));
  return true;
};
